import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { fetchCalendar, SIZES, toISODate } from "@/lib/noleggio";
import { useI18n } from "@/lib/i18n";

export const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/** Monday-first grid of the given month: null for leading blanks, ISO date strings otherwise. */
export function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;
  const n = new Date(year, month + 1, 0).getDate();
  return [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => toISODate(new Date(year, month, i + 1)))];
}

export function MonthHeader({ cursor, setCursor, minMonth, maxMonth, prefix }) {
  const { t } = useI18n();
  const months = t("months_long").split(",");
  const prevOk = !minMonth || monthKey(cursor) > minMonth;
  const nextOk = !maxMonth || monthKey(cursor) < maxMonth;
  const move = (d) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + d, 1));
  return (
    <div className="flex items-center justify-between mb-3">
      <button data-testid={`${prefix}-prev-month`} disabled={!prevOk} onClick={() => move(-1)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--surface-2)] disabled:opacity-30"><ChevronLeft size={20} /></button>
      <p data-testid={`${prefix}-month-label`} className="font-extrabold">{months[cursor.getMonth()]} {cursor.getFullYear()}</p>
      <button data-testid={`${prefix}-next-month`} disabled={!nextOk} onClick={() => move(1)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--surface-2)] disabled:opacity-30"><ChevronRight size={20} /></button>
    </div>
  );
}

export function WeekdayRow() {
  const { t } = useI18n();
  return <div className="grid grid-cols-7 gap-1 mb-1">{t("wd").split(",").map((d) => <span key={d} className="text-[11px] font-bold text-center text-[var(--muted)] uppercase">{d}</span>)}</div>;
}

export default function AvailabilityCalendar({ bikeId, size, value, onChange }) {
  const { t } = useI18n();
  const today = toISODate(new Date());
  const [cursor, setCursor] = useState(() => { const [y, m] = value.split("-").map(Number); return new Date(y, m - 1, 1); });
  const key = monthKey(cursor);
  const q = useQuery({ queryKey: ["calendar", key], queryFn: () => fetchCalendar(key), staleTime: 0 });
  const cells = monthGrid(cursor.getFullYear(), cursor.getMonth());

  const freeOn = (iso) => {
    const a = q.data?.days?.[iso]?.[bikeId];
    if (!a) return null;
    return size ? a[size] ?? 0 : SIZES.reduce((s, k) => s + (a[k] ?? 0), 0);
  };

  return (
    <div data-testid="availability-calendar" className="bg-white rounded-2xl p-4">
      <MonthHeader cursor={cursor} setCursor={setCursor} minMonth={today.slice(0, 7)} maxMonth={monthKey(new Date(new Date().getFullYear(), new Date().getMonth() + 12, 1))} prefix="calendar" />
      <WeekdayRow />
      <div className="grid grid-cols-7 gap-1 relative">
        {cells.map((iso, i) => {
          if (!iso) return <span key={`b${i}`} />;
          const past = iso < today;
          const n = past ? null : freeOn(iso);
          const full = n === 0;
          const on = iso === value;
          const tone = on ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]"
            : past ? "text-[#c7c7cc] border-transparent"
            : full ? "bg-[var(--surface-2)] text-[var(--muted)] border-transparent line-through"
            : n !== null && n <= 1 ? "bg-amber-50 border-amber-200 hover:border-amber-400"
            : "bg-[var(--brand-tertiary)] border-transparent hover:border-[var(--brand)]";
          return (
            <button key={iso} data-testid={`date-${iso}`} disabled={past || full} onClick={() => onChange(iso)}
              className={`h-14 rounded-xl border flex flex-col items-center justify-center transition-colors disabled:cursor-not-allowed ${tone}`}>
              <span className="text-[15px] font-extrabold leading-none">{Number(iso.slice(8))}</span>
              {!past && n !== null && (
                <span data-testid={`date-free-${iso}`} className={`text-[10px] font-bold mt-1 leading-none ${on ? "" : full ? "" : n <= 1 ? "text-[#c97a00]" : "text-[var(--brand-primary)]"}`}>
                  {full ? "—" : n}
                </span>
              )}
            </button>
          );
        })}
        {q.isLoading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center gap-2 text-sm text-[var(--muted)]"><Loader2 size={16} className="animate-spin" /> {t("cal_loading")}</div>}
      </div>
      <p className="text-xs text-[var(--muted)] mt-3">{size ? t("cal_hint_size", { s: size }) : t("cal_hint_all")}</p>
    </div>
  );
}
